import { Head, Link, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Separator } from "@/components/ui/separator"
import { PageHeader } from "@/components/layouts/PageHeader"
import { CapturesTable } from "@/components/captures/CapturesTable"
import { cn } from "@/lib/utils"
import { timeAgo, formatHour, useTimezone } from "@/lib/date"
import { Pencil, Trash2, Copy, Check, RefreshCw } from "lucide-react"
import { useState } from "react"
import type { Source, Capture, SourceType } from "@/types"

const SOURCE_TYPE_LABELS: Record<SourceType, string> = {
  webhook: "Webhook",
  rss: "RSS",
  podcast_rss: "Podcast RSS",
  mimeo: "Mimeo",
  youtube: "YouTube",
  discord: "Discord",
}

function formatPollingInterval(minutes: number | null): string {
  if (!minutes) return "Not set"
  if (minutes % 525600 === 0) return `${minutes / 525600} year(s)`
  if (minutes % 43200 === 0) return `${minutes / 43200} month(s)`
  if (minutes % 10080 === 0) return `${minutes / 10080} week(s)`
  if (minutes % 1440 === 0) return `${minutes / 1440} day(s)`
  if (minutes % 60 === 0) return `${minutes / 60} hour(s)`
  return `${minutes} minutes`
}

interface SourceShowProps {
  source: Source
  captures: Capture[]
  webhook_url?: string
}

export default function Show({ source, captures, webhook_url }: SourceShowProps) {
  const timezone = useTimezone()
  const [copied, setCopied] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteCapturesOpen, setDeleteCapturesOpen] = useState(false)

  function copyWebhookUrl() {
    if (webhook_url) {
      navigator.clipboard.writeText(webhook_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  function toggleEnabled() {
    router.post(`/admin/sources/${source.id}/toggle`, {}, { preserveScroll: true })
  }

  function handleDelete() {
    router.delete(`/admin/sources/${source.id}`)
  }

  function handlePollNow() {
    router.post(`/admin/sources/${source.id}/poll_now`, {}, { preserveScroll: true })
  }

  function handleDeleteCaptures() {
    router.delete(`/admin/sources/${source.id}/delete_captures`, { preserveScroll: true })
    setDeleteCapturesOpen(false)
  }

  const isPoller = source.source_type !== "webhook"

  return (
    <>
      <Head title={source.name} />
      <div className="space-y-6">
        <PageHeader title={source.name}>
          <Button variant="outline" asChild>
            <Link href={`/admin/sources/${source.id}/edit`}>
              <Pencil className="size-4" />
              Edit
            </Link>
          </Button>
        </PageHeader>

        <Card>
          <CardHeader>
            <CardTitle>Source Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">
                {SOURCE_TYPE_LABELS[source.source_type]}
              </Badge>
              <Badge
                variant="outline"
                className={cn(
                  source.category === "owned"
                    ? "border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400"
                    : "border-purple-300 text-purple-700 dark:border-purple-700 dark:text-purple-400"
                )}
              >
                {source.category === "owned" ? "Owned" : "Watched"}
              </Badge>
              <Badge variant={source.enabled ? "default" : "secondary"}>
                {source.enabled ? "Enabled" : "Disabled"}
              </Badge>
            </div>

            {source.source_type === "webhook" && webhook_url && (
              <div className="space-y-1">
                <p className="text-sm font-medium">Webhook URL</p>
                <div className="bg-muted flex items-center gap-2 rounded-md p-3">
                  <code className="flex-1 break-all text-sm">{webhook_url}</code>
                  <Button variant="ghost" size="icon-sm" onClick={copyWebhookUrl}>
                    {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                  </Button>
                </div>
              </div>
            )}

            {isPoller && (
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Polling interval: </span>
                  <span className="font-medium">{formatPollingInterval(source.polling_interval)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Last polled: </span>
                  <span className="font-medium">{timeAgo(source.last_polled_at)}</span>
                </div>
                {source.poll_time_of_day !== null && source.poll_time_of_day !== undefined && (
                  <div>
                    <span className="text-muted-foreground">Poll time: </span>
                    <span className="font-medium">{formatHour(source.poll_time_of_day, timezone)}</span>
                  </div>
                )}
                <Button variant="outline" size="sm" onClick={handlePollNow}>
                  <RefreshCw className="size-4" />
                  Poll Now
                </Button>
              </div>
            )}

            {Object.keys(source.configuration).length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium">Configuration</p>
                <div className="bg-muted rounded-md p-3">
                  {Object.entries(source.configuration).map(([key, value]) => (
                    <div key={key} className="flex gap-2 text-sm">
                      <span className="text-muted-foreground">{key}:</span>
                      <span className="break-all">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1">
              <p className="text-sm font-medium">Delivery</p>
              <div className="flex flex-wrap gap-2">
                {source.deliver_to_webhook && <Badge variant="secondary">Webhook</Badge>}
                {source.deliver_to_github && <Badge variant="secondary">GitHub</Badge>}
                {!source.deliver_to_webhook && !source.deliver_to_github && (
                  <span className="text-muted-foreground text-sm">None</span>
                )}
              </div>
              {source.deliver_to_github && source.github_path_template && (
                <p className="text-muted-foreground font-mono text-sm">
                  {source.github_path_template}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {source.payload_mappings.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Payload Mappings</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Key</TableHead>
                    <TableHead>Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {source.payload_mappings.map((mapping, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{mapping.key}</TableCell>
                      <TableCell className="font-mono text-sm">{mapping.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>Recent Captures</CardTitle>
            {captures.length > 0 && (
              <Dialog open={deleteCapturesOpen} onOpenChange={setDeleteCapturesOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Trash2 className="size-4" />
                    Delete All
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete All Captures</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to delete all {source.captures_count} captures for "{source.name}"? This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDeleteCapturesOpen(false)}>
                      Cancel
                    </Button>
                    <Button variant="destructive" onClick={handleDeleteCaptures}>
                      Delete All
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </CardHeader>
          <CardContent>
            <CapturesTable captures={captures} />
          </CardContent>
        </Card>

        <Separator />

        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={toggleEnabled}>
            {source.enabled ? "Disable Source" : "Enable Source"}
          </Button>

          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <DialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="size-4" />
                Delete Source
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Source</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete "{source.name}"? This will also delete all
                  associated captures. This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  )
}
